import { RuleId } from '@wix/a11y-rules';
import { DEFAULT_BRANCH_ID } from '@wix/a11y-wizard-common';
import { Box } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { useOnUnmount } from '@wix/rules-panel-ui';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { altTextScreenPageIdAtom, panelStateAtom, scrollerNodeRefAtom, totalIssuesCountAtom, unsavedChangedHandlersAtom, verticalsDrillInPageIdAtom } from '../atoms/session-atoms';
import { BiCtaNames } from '../bi/types';
import { AltTextScreenWrapper } from '../components/fixers/alt-image-fixer/drill-in/alt-text-screen/alt-text-screen-wrapper';
import { showSaveBeforeCloseModal } from '../components/modals/show-save-before-leave-modal';
import { VerticalsDrillInScreen } from '../components/verticals-drill-in/verticals-drill-in-screen';
import { ManualTasksList } from '../components/wizard-content/content-results/manual-tasks';
import { WizardPanelContentResults } from '../components/wizard-content/content-results/wizard-panel-content-results';
import { WizardPanelContentWelcome } from '../components/wizard-content/content-welcome/wizard-panel-content-welcome';
import { PanelLoader } from '../components/wizard-content/panel-loader';
import { WizardFooter } from '../components/wizard-footer/wizard-footer';
import { WizardHeader } from '../components/wizard-header/wizard-header';
import { ACCESSIBILITY_PERMISSIONS, SITE_LEVEL_PAGE_ID } from '../consts';
import { useDuplexer } from '../duplexer/use-duplexer';
import { useGetDuplexerEventHandlers } from '../duplexer/use-get-duplexer-event-handlers';
import { usePanelStateAtom } from '../hooks/rules-state';
import { useScanTimeoutManager } from '../hooks/scan-timeout';
import { useA11yWizardAdapter } from '../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../hooks/use-bi-logger';
import { useOnMount } from '../hooks/use-on-mount';
import { useOnScanFinished } from '../hooks/use-on-scan-finished';
import { useRuleGroupsBuilderV2 } from '../hooks/use-rule-groups-builder-v2';
import { useScanSiteBiEvents } from '../hooks/use-scan-site-bi-events';
import { useSessionInfoContext } from '../hooks/use-session-info-context';
import { PanoramaTransactions } from '../panorama/constants';
import { usePanoramaFunctions } from '../panorama/use-panorama-functions';
import { useAnalyzeFullSiteQuery } from '../queries/use-analyze-full-site-query';
import { useFetchRulesData } from '../queries/use-fetch-rules-data-query';
export const A11yWizardPanel = () => {
  var _getGroupData;
  const unsavedChangedHandlersValue = useAtomValue(unsavedChangedHandlersAtom);
  const {
    experiments
  } = useExperiments();
  const {
    publishedBranchId,
    branchId: currentBranchId,
    userPermissions
  } = useSessionInfoContext();
  const duplexerEventHandlers = useGetDuplexerEventHandlers();
  useDuplexer(duplexerEventHandlers);
  const {
    clearAllScanTimeouts,
    initScanTimeouts
  } = useScanTimeoutManager();
  const {
    editorHost,
    onPanelStateChange,
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const altTextScreenPageId = useAtomValue(altTextScreenPageIdAtom);
  const panelState = useAtomValue(panelStateAtom);
  const {
    groupsDataMap
  } = panelState;
  const {
    t
  } = useTranslation();
  const {
    getGroupData
  } = usePanelStateAtom();
  const totalIssuesCount = useAtomValue(totalIssuesCountAtom);
  const {
    startPanoramaTransaction,
    finishPanoramaTransaction
  } = usePanoramaFunctions();
  const {
    refetch: analyzeFullSite
  } = useAnalyzeFullSiteQuery();
  const ruleGroupsV2 = useRuleGroupsBuilderV2();
  const biLogger = useBiLogger();
  const {
    data: rulesData,
    isLoading: isFirstFetching
  } = useFetchRulesData();
  // Fallback if rulesData is not available, then assume that DOM order was completed
  const wasDomOrderCompleted = (rulesData == null ? void 0 : rulesData.some(ruleData => ruleData.pageId === SITE_LEVEL_PAGE_ID && ruleData.ruleId === RuleId.DOM_ORDER)) ?? true;
  const sendBiLoggerScanEvents = useScanSiteBiEvents();
  const verticalsDrillInPageId = useAtomValue(verticalsDrillInPageIdAtom);
  const isPublishedBranch = !publishedBranchId && currentBranchId === DEFAULT_BRANCH_ID || publishedBranchId === currentBranchId;
  const manualTasks = ManualTasksList();
  const nodeRef = useRef(null);
  const setNodeRef = useSetAtom(scrollerNodeRefAtom);
  setNodeRef(nodeRef);
  const altTextScreenData = (_getGroupData = getGroupData((altTextScreenPageId == null ? void 0 : altTextScreenPageId.pageId) ?? '')) == null ? void 0 : _getGroupData.ruleExecutionResults;
  const showAltTextScreen = useMemo(() => altTextScreenPageId !== undefined && altTextScreenData !== undefined, [altTextScreenData, altTextScreenPageId]);
  const renderEmptyState = groupsDataMap.size === 0;
  const hasSavePermission = a11yWizardAdapter.getHasSavePermission(); // TODO: remove this once we migrate specs.a11y.CheckAnalyzePermissions
  const hasAnalyzePermissions = useMemo(() => {
    const permissionsSet = new Set(userPermissions);
    return Object.values(ACCESSIBILITY_PERMISSIONS).every(permission => permissionsSet.has(permission));
  }, [userPermissions]);
  const hasPermissions = experiments.enabled('specs.a11y.CheckAnalyzePermissions') ? hasAnalyzePermissions : hasSavePermission;
  useOnUnmount(() => {
    biLogger.wizardPanelClose();
    clearAllScanTimeouts(expiresAtMap => {
      onPanelStateChange == null || onPanelStateChange({
        ...panelState,
        initialScanTimeoutExpiresAtMap: expiresAtMap
      });
    });
  });
  useOnMount(() => {
    initScanTimeouts(panelState.initialScanTimeoutExpiresAtMap ?? new Map());
    biLogger.wizardPanelOpen();
  });
  useEffect(() => {
    onPanelStateChange == null || onPanelStateChange(panelState);
  }, [panelState, onPanelStateChange]);
  useOnScanFinished(() => {
    // TODO: check if we still need these event with new infra
    sendBiLoggerScanEvents();
  });
  const handleAnalyzeFullSiteClick = useCallback(async () => {
    startPanoramaTransaction(PanoramaTransactions.analyzeSite);
    biLogger.ctaClick({
      ctaName: BiCtaNames.ScanButton
    });
    await analyzeFullSite();
    finishPanoramaTransaction(PanoramaTransactions.analyzeSite);
  }, [analyzeFullSite, biLogger, finishPanoramaTransaction, startPanoramaTransaction]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    height: "100%",
    minHeight: "560px",
    overflow: "hidden",
    width: "100%"
  }, showAltTextScreen ? /*#__PURE__*/React.createElement(AltTextScreenWrapper, {
    itemType: altTextScreenPageId.itemType,
    pageId: altTextScreenPageId.pageId
  }) : verticalsDrillInPageId ? /*#__PURE__*/React.createElement(VerticalsDrillInScreen, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(WizardHeader, {
    detectedIssuesNumber: totalIssuesCount,
    isEmptyState: renderEmptyState,
    manualTasksNumber: manualTasks.length,
    onClickClose: () => {
      biLogger.ctaClick({
        ctaName: BiCtaNames.CloseTheWizard
      });
      if (unsavedChangedHandlersValue != null && unsavedChangedHandlersValue.exists) {
        showSaveBeforeCloseModal({
          a11yWizardAdapter,
          biLogger,
          editorHost,
          handleApply: unsavedChangedHandlersValue.apply,
          handleDiscard: () => a11yWizardAdapter.togglePanel(),
          t
        });
      } else {
        a11yWizardAdapter.togglePanel();
      }
    },
    onRescanClick: () => {
      biLogger.ctaClick({
        ctaName: BiCtaNames.MenuScanAgain
      });
      handleAnalyzeFullSiteClick();
    }
  }), isFirstFetching ? /*#__PURE__*/React.createElement(PanelLoader, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: nodeRef,
    style: {
      flex: '1 1 auto',
      minHeight: '280px',
      overflowX: 'hidden',
      overflowY: 'auto'
    }
  }, renderEmptyState ? /*#__PURE__*/React.createElement(WizardPanelContentWelcome, {
    hasSavePermission: hasPermissions,
    isPublishedBranch: isPublishedBranch
  }) : /*#__PURE__*/React.createElement(WizardPanelContentResults, {
    manualTasks: manualTasks,
    ruleGroups: ruleGroupsV2,
    togglePanel: a11yWizardAdapter.togglePanel
  })), /*#__PURE__*/React.createElement(WizardFooter, {
    hasSavePermission: hasPermissions,
    isEmptyState: renderEmptyState,
    isPublishedBranch: isPublishedBranch,
    wasDomOrderCompleted: wasDomOrderCompleted
  }))));
};
//# sourceMappingURL=a11y-wizard-panel.js.map